/**
 * Admin Field Agents Management — Release 2 (2026-04-23)
 * /admin/field-agents
 *
 * List · Create · Edit · Activate/Deactivate · Activity counts
 */
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Loader2, Plus, Users, Edit3, Power, ArrowLeft, CheckCircle2, Activity, Phone, Mail, ShieldCheck, TrendingUp, TrendingDown, Zap,
} from "lucide-react";
import AdminContractHealthCard from "../../components/admin/AdminContractHealthCard";
import AdminContractPatchForm from "../../components/admin/AdminContractPatchForm";


export default function AdminFieldAgents() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeOnly, setActiveOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [contractAgent, setContractAgent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeOnly) params.set("active_only", "true");
      // Fetch both list (gives activity block) and productivity (gives contract status_label)
      const [listRes, prodRes] = await Promise.all([
        api.get(`/admin/field-agents?${params.toString()}`),
        api.get(`/admin/field-agents/productivity`).catch(() => ({ data: { agents: [] } })),
      ]);
      const paceMap = {};
      for (const a of (prodRes.data.agents || [])) {
        paceMap[a.id] = a.contract?.status_label;
      }
      const merged = (listRes.data.agents || []).map((a) => ({ ...a, pace: paceMap[a.id] || null }));
      setRows(merged);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  useEffect(() => { load(); }, [load]);

  const onActivate = async (id) => {
    try { await api.post(`/admin/field-agents/${id}/activate`); toast.success("Activated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Activate failed"); }
  };
  const onDeactivate = async (id) => {
    if (!window.confirm("Deactivate this field agent? They will lose portal access until reactivated.")) return;
    try { await api.post(`/admin/field-agents/${id}/deactivate`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Deactivate failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-field-agents-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="admin-agents-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-7 h-7 text-indigo-600" /> Field Agents
            </h1>
            <p className="text-slate-500 text-sm">Create, edit, activate/deactivate field-onboarding agents. Activity counts are live.</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/admin/field-activities/review")} data-testid="open-review-queue-btn" className="mr-2">Review queue</Button>
          <Button onClick={() => setCreating(true)} data-testid="admin-agent-create-btn">
            <Plus className="w-4 h-4 mr-1" /> New Agent
          </Button>
        </div>

        <AdminContractHealthCard agents={rows} />
        <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} data-testid="admin-agent-active-only" />
            Active only
          </label>
          <span className="text-xs text-slate-400 ml-auto">{rows.length} agent{rows.length === 1 ? "" : "s"}</span>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-slate-400">No field agents yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3">Agent</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">Submissions</th>
                  <th className="text-left px-4 py-3">Approvals</th>
                  <th className="text-left px-4 py-3">Last activity</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const act = a.activity || {};
                  const subs = act.total_submissions ?? act.stats_candidates_submitted ?? 0;
                  const appr = act.pudo_approved ?? act.stats_candidates_approved ?? 0;
                  const last = act.stats_last_candidate_at || a.last_login_at || null;
                  return (
                    <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-agent-row-${a.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-800">{a.name || "(unnamed)"}</div>
                        <div className="text-xs text-slate-400 font-mono">{a.id}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{a.phone || "—"}</div>
                        {a.email && <div className="flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3 text-slate-400" />{a.email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold" data-testid={`admin-agent-subs-${a.id}`}>{subs}</div>
                        <div className="text-[10px] text-slate-400">
                          P{act.pudo_submissions ?? 0} · D{act.driver_submissions ?? 0} · S{act.seller_submissions ?? 0}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-emerald-700" data-testid={`admin-agent-appr-${a.id}`}>{appr}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {last ? <span className="inline-flex items-center gap-1"><Activity className="w-3 h-3 text-emerald-500" />{last.slice(0, 16).replace("T", " ")}</span> : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {a.is_active ? (
                          <Badge className="bg-emerald-500/15 text-emerald-700 text-xs">active</Badge>
                        ) : (
                          <Badge className="bg-slate-500/15 text-slate-600 text-xs">inactive</Badge>
                        )}
                        {a.can_approve_on_spot && (
                          <Badge className="ml-1 bg-indigo-500/15 text-indigo-700 text-xs inline-flex items-center gap-1"><button data-testid={`open-contract-btn-${a.id}`} className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 mr-1" onClick={() => setContractAgent(a)}>Contract</button><ShieldCheck className="w-3 h-3" />approver</Badge>
                        )}
                        <PaceChip label={a.pace} id={a.id} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => navigate(`/admin/field-agents/${a.id}/productivity`)} data-testid={`admin-agent-productivity-${a.id}`} title="Productivity"><TrendingUp className="w-3 h-3 text-indigo-600" /></Button>
                          <Button size="sm" variant="outline" onClick={() => setEditing(a)} data-testid={`admin-agent-edit-${a.id}`}><Edit3 className="w-3 h-3" /></Button>
                          {a.is_active ? (
                            <Button size="sm" variant="outline" onClick={() => onDeactivate(a.id)} data-testid={`admin-agent-deactivate-${a.id}`}><Power className="w-3 h-3 text-amber-600" /></Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => onActivate(a.id)} data-testid={`admin-agent-activate-${a.id}`}><Power className="w-3 h-3 text-emerald-600" /></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {creating && <CreateAgentModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {editing && <EditAgentModal row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}


function PaceChip({ label, id }) {
  if (!label || label === "no_contract") return null;
  const map = {
    ahead:    { cls: "bg-emerald-500/15 text-emerald-700",   icon: Zap,           text: "AHEAD" },
    on_pace:  { cls: "bg-sky-500/15 text-sky-700",           icon: TrendingUp,    text: "ON PACE" },
    behind:   { cls: "bg-rose-500/15 text-rose-700",         icon: TrendingDown,  text: "BEHIND" },
  };
  const conf = map[label];
  if (!conf) return null;
  const Icon = conf.icon;
  return (
    <Badge
      className={`ml-1 ${conf.cls} text-[10px] px-1.5 py-0.5 inline-flex items-center gap-1`}
      data-testid={`admin-agent-pace-${id}`}
    >
      <Icon className="w-3 h-3" /> {conf.text}
    </Badge>
  );
}

function CreateAgentModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", can_approve_on_spot: false });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!form.name || !form.phone) { toast.error("Name and phone required"); return; }
    setSubmitting(true);
    try { await api.post("/admin/field-agents", form); toast.success("Field agent created"); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-agent-create-modal">
        <h3 className="text-xl font-bold mb-4">New Field Agent</h3>
        <div className="space-y-3 text-sm">
          <div><label className="text-xs text-slate-500">Name</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-agent-create-name" /></div>
          <div><label className="text-xs text-slate-500">Phone (no +, digits only)</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="admin-agent-create-phone" /></div>
          <div><label className="text-xs text-slate-500">Email (optional)</label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-agent-create-email" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.can_approve_on_spot} onChange={(e) => setForm({ ...form, can_approve_on_spot: e.target.checked })} data-testid="admin-agent-create-approver" /> Can approve on the spot</label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="admin-agent-create-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Create
          </Button>
        </div>
      </div>
      <AdminContractPatchForm agent={contractAgent} open={!!contractAgent} onClose={() => setContractAgent(null)} onSaved={() => { setContractAgent(null); load(); }} />
    </div>
  );
}

function EditAgentModal({ row, onClose, onSaved }) {
  const [form, setForm] = useState({ name: row.name || "", email: row.email || "", can_approve_on_spot: !!row.can_approve_on_spot });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try { await api.patch(`/admin/field-agents/${row.id}`, form); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-agent-edit-modal">
        <h3 className="text-xl font-bold mb-4">Edit · {row.name}</h3>
        <div className="space-y-3 text-sm">
          <div><label className="text-xs text-slate-500">Name</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-agent-edit-name" /></div>
          <div><label className="text-xs text-slate-500">Email</label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-agent-edit-email" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.can_approve_on_spot} onChange={(e) => setForm({ ...form, can_approve_on_spot: e.target.checked })} data-testid="admin-agent-edit-approver" /> Can approve on the spot</label>
          <p className="text-xs text-slate-400">Phone is immutable after creation. Use phone migration for re-assignment.</p>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="admin-agent-edit-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save
          </Button>
        </div>
      </div>
    </div>
  );
}
